const en = {
  title: 'Set up your marketing',
  intro: 'Your business. Your goals. One connected workspace.',
  welcome: 'Choose your setup path',
  business: 'Your business',
  goals: 'Growth priorities',
  accounts: 'Accounts & data',
  brief: 'Marketing brief',
  review: 'Review your setup',
  guided: 'Guide my setup',
  guidedHint: 'A few focused steps to get your workspace ready.',
  manual: 'I’ll set up myself',
  manualHint: 'Go to your dashboard and connect accounts when you are ready.',
  continue: 'Save & continue',
  back: 'Back',
  later: 'Save & finish later',
  saved: 'Progress saved',
  save: 'Save changes',
  loading: 'Loading your setup…',
  error: 'We could not save this step. Check your details and try again.',
  conflict: 'This setup changed in another window. Reload before saving again.',
  reload: 'Reload saved progress',
  name: 'Business name',
  industry: 'Industry',
  website: 'Website (optional)',
  market: 'Primary market',
  timezone: 'Time zone',
  languages: 'Content languages',
  selectGoals: 'Choose up to two priorities. You can change them later.',
  leads: 'More inbound leads',
  bookings: 'More bookings or visits',
  sales: 'More online sales',
  visibility: 'Better search visibility',
  retention: 'Customer retention',
  connect: 'Connect account',
  connectHint:
    'Authorize on the provider’s own page, then choose the business assets to connect. Nothing publishes automatically.',
  unconnected: 'Not connected',
  connected: 'Connected — check sync readiness',
  attention: 'Needs attention',
  manualData: 'Use manual information',
  manualHintData: 'Profile URLs and notes are manually supplied, not measured account performance.',
  sourceName: 'Source name',
  sourceUrl: 'Public profile or source URL',
  notes: 'Manual notes',
  addSource: 'Add source',
  remove: 'Remove',
  import: 'Upload files securely',
  importHint:
    'Your progress is saved before opening the existing asset library. Return here from Setup.',
  knowledge: 'Knowledge & business files',
  services: 'What do you sell or provide?',
  audience: 'Who should your marketing reach?',
  offer: 'Current offer (optional)',
  tone: 'Preferred tone',
  reviewHint: 'This is a draft setup, not an audit result or a launched campaign.',
  draftTitle: 'Your first-week priorities',
  draft1: 'Establish a baseline from the data you make available.',
  draft2: 'Draft priorities and content around your selected goals.',
  draft3: 'Review and approve work before anything is published or any money is spent.',
  approve: 'No publishing, ads or spending without your approval.',
  finish: 'Finish setup',
  dashboard: 'Go to dashboard',
  checklist: 'Finish your marketing setup',
  resume: 'Continue setup',
  remind: 'Email me a resume link',
  reminded: 'Resume link requested. Check your inbox.',
  empty: 'Not provided yet',
  optional: 'Optional',
  phoneTitle: 'Verify your WhatsApp number',
  phoneIntro:
    'We use this code only to verify your contact number. It does not connect WhatsApp Business or subscribe you to marketing.',
  phone: 'WhatsApp number',
  country: 'Country code',
  consent: 'Send a verification code to this number on WhatsApp.',
  sendCode: 'Send code via WhatsApp',
  code: '6-digit verification code',
  verify: 'Verify number',
  resend: 'Resend code',
  changeNumber: 'Change number',
  verified: 'Number verified',
  unavailable: 'WhatsApp verification is not available yet. Your number is not marked verified.',
  assistance: 'Request verification assistance',
  assistanceSent: 'Your assistance request is saved. Verification is still pending.',
  wrongCode:
    'The code is invalid, expired or has too many attempts. Request a fresh code when the timer allows.',
  deliveryError: 'The code could not be sent. Try again later or request assistance.',
  cooldown: 'Please wait before requesting another code.',
  sentTo: 'Code requested for',
  expires: 'Code expires in',
  seconds: 'seconds',
  manualStatus: 'Manual setup — checklist remains open',
  completed: 'Setup complete',
  connectionError:
    'This account could not be connected. Your setup has been saved; try again from Integrations.',
  integrations: 'Manage all integrations',
  waBusiness: 'WhatsApp Business',
  waBusinessHint: 'Business account setup is separate from verifying your personal contact number.',
  noChanges: 'No active account connections are required to finish the marketing brief.',
  needGoals: 'Enter a business name and choose at least one goal before finishing.',
  pendingPhone: 'Verify your phone before completing setup.',
  security: 'Account security',
  securityHint: 'Finish your required authenticator or passkey setup before continuing.',
  securityLink: 'Continue secure sign-in',
};
const ar: Record<keyof typeof en, string> = {
  title: 'أعِدّ تسويق أعمالك',
  intro: 'أعمالك وأهدافك في مساحة عمل واحدة مترابطة.',
  welcome: 'اختر طريقة الإعداد',
  business: 'معلومات أعمالك',
  goals: 'أولويات النمو',
  accounts: 'الحسابات والبيانات',
  brief: 'موجز التسويق',
  review: 'راجع الإعداد',
  guided: 'أرشدني خلال الإعداد',
  guidedHint: 'خطوات مركّزة لتجهيز مساحة عملك.',
  manual: 'سأُعدّ الحساب بنفسي',
  manualHint: 'انتقل إلى لوحة التحكم واربط حساباتك عندما تكون جاهزاً.',
  continue: 'حفظ ومتابعة',
  back: 'رجوع',
  later: 'حفظ والإكمال لاحقاً',
  saved: 'تم حفظ التقدم',
  save: 'حفظ التغييرات',
  loading: 'جارٍ تحميل الإعداد…',
  error: 'تعذّر حفظ هذه الخطوة. راجع بياناتك وحاول مجدداً.',
  conflict: 'تغيّر الإعداد في نافذة أخرى. أعد التحميل قبل الحفظ.',
  reload: 'إعادة تحميل التقدم المحفوظ',
  name: 'اسم النشاط التجاري',
  industry: 'القطاع',
  website: 'الموقع الإلكتروني (اختياري)',
  market: 'السوق الرئيسية',
  timezone: 'المنطقة الزمنية',
  languages: 'لغات المحتوى',
  selectGoals: 'اختر أولويتين كحد أقصى. يمكنك تغييرهما لاحقاً.',
  leads: 'زيادة العملاء المحتملين',
  bookings: 'زيادة الحجوزات أو الزيارات',
  sales: 'زيادة المبيعات عبر الإنترنت',
  visibility: 'تحسين الظهور في البحث',
  retention: 'الحفاظ على العملاء',
  connect: 'ربط الحساب',
  connectHint:
    'امنح الإذن في صفحة مزوّد الخدمة، ثم اختر أصول النشاط التي تريد ربطها. لن يُنشر أي شيء تلقائياً.',
  unconnected: 'غير متصل',
  connected: 'متصل — تحقّق من جاهزية المزامنة',
  attention: 'يحتاج إلى مراجعة',
  manualData: 'استخدام معلومات يدوية',
  manualHintData: 'الروابط والملاحظات مدخلة يدوياً وليست قياسات فعلية لأداء الحساب.',
  sourceName: 'اسم المصدر',
  sourceUrl: 'رابط الملف العام أو مصدر البيانات',
  notes: 'ملاحظات يدوية',
  addSource: 'إضافة مصدر',
  remove: 'إزالة',
  import: 'رفع الملفات بأمان',
  importHint: 'نحفظ تقدمك قبل فتح مكتبة الأصول الحالية. يمكنك العودة من صفحة الإعداد.',
  knowledge: 'المعرفة وملفات الأعمال',
  services: 'ما المنتجات أو الخدمات التي تقدّمها؟',
  audience: 'من الجمهور الذي تريد الوصول إليه؟',
  offer: 'العرض الحالي (اختياري)',
  tone: 'نبرة المحتوى المفضّلة',
  reviewHint: 'هذا إعداد مبدئي، وليس نتيجة تدقيق أو حملة تم إطلاقها.',
  draftTitle: 'أولويات الأسبوع الأول',
  draft1: 'تحديد نقطة البداية باستخدام البيانات التي تتيحها.',
  draft2: 'صياغة الأولويات والمحتوى وفق أهدافك المختارة.',
  draft3: 'مراجعة العمل والموافقة عليه قبل النشر أو الإنفاق.',
  approve: 'لا نشر ولا إعلانات ولا إنفاق دون موافقتك.',
  finish: 'إكمال الإعداد',
  dashboard: 'الانتقال إلى لوحة التحكم',
  checklist: 'أكمل إعداد التسويق',
  resume: 'متابعة الإعداد',
  remind: 'أرسل لي رابط المتابعة بالبريد',
  reminded: 'تم طلب إرسال رابط المتابعة. راجع بريدك.',
  empty: 'لم تُضف بعد',
  optional: 'اختياري',
  phoneTitle: 'تحقّق من رقم واتساب',
  phoneIntro:
    'نستخدم الرمز للتحقق من رقم التواصل فقط. لا يربط واتساب للأعمال ولا يشترك بك في الرسائل التسويقية.',
  phone: 'رقم واتساب',
  country: 'رمز الدولة',
  consent: 'أرسل رمز التحقق إلى هذا الرقم عبر واتساب.',
  sendCode: 'إرسال الرمز عبر واتساب',
  code: 'رمز تحقق من 6 أرقام',
  verify: 'تحقّق من الرقم',
  resend: 'إعادة إرسال الرمز',
  changeNumber: 'تغيير الرقم',
  verified: 'تم التحقق من الرقم',
  unavailable: 'التحقق عبر واتساب غير متاح حالياً. لم يتم تأكيد رقمك.',
  assistance: 'طلب المساعدة في التحقق',
  assistanceSent: 'تم حفظ طلب المساعدة. التحقق ما زال معلقاً.',
  wrongCode:
    'الرمز غير صحيح أو منتهي الصلاحية أو تجاوز عدد المحاولات. اطلب رمزاً جديداً بعد انتهاء المؤقت.',
  deliveryError: 'تعذّر إرسال الرمز. حاول لاحقاً أو اطلب المساعدة.',
  cooldown: 'انتظر قليلاً قبل طلب رمز جديد.',
  sentTo: 'طُلب إرسال الرمز إلى',
  expires: 'تنتهي صلاحية الرمز خلال',
  seconds: 'ثانية',
  manualStatus: 'إعداد يدوي — قائمة الخطوات غير مكتملة',
  completed: 'اكتمل الإعداد',
  connectionError: 'تعذّر ربط هذا الحساب. تم حفظ الإعداد؛ حاول مجدداً من صفحة التكاملات.',
  integrations: 'إدارة جميع التكاملات',
  waBusiness: 'واتساب للأعمال',
  waBusinessHint: 'إعداد حساب الأعمال منفصل عن التحقق من رقم التواصل الشخصي.',
  noChanges: 'لا يلزم ربط حسابات لإكمال موجز التسويق.',
  needGoals: 'أدخل اسم النشاط واختر هدفاً واحداً على الأقل قبل الإكمال.',
  pendingPhone: 'تحقّق من رقمك قبل إكمال الإعداد.',
  security: 'أمان الحساب',
  securityHint: 'أكمل إعداد تطبيق المصادقة أو مفتاح المرور المطلوب قبل المتابعة.',
  securityLink: 'متابعة تسجيل الدخول الآمن',
};
export const setupCopy = (locale: string) => (locale === 'ar' ? ar : en);
